/**
 * Media Management - Moon Moon Agency
 * Handles video lazy loading with viewport-based logic
 */

/*********************************************************************************************
 * 🎥 VIDEO MODULE
 */
const VideoManager = {
	// Local configuration
	config: {
		selectors: {
			videoLazy: '.mm-video-lazy',
			videoSource: 'source'
		},
		classes: {
			videoLoaded: 'mm-video-loaded'
		},
		timing: {
			resizeDebounce: 150,
			// Max wait for the poster before loading the video anyway
			posterTimeout: 3000
		}
	},

	// Stored resize handler reference for proper cleanup
	_resizeHandler: null,

	/**
	 * Initialize lazy video loading
	 */
	init() {
		const videos = window.getElements(this.config.selectors.videoLazy);
		if (!videos.length) return;

		/**
		 * Wait for the poster to be decoded before pulling the video bytes.
		 * On a hero, the poster IS the LCP paint: letting a multi-MB video download
		 * alongside it starves the LCP resource on mobile connections.
		 * Bounded by a timeout so a missing/broken poster can never block playback.
		 */
		const whenPosterReady = (video) => {
			const poster = video.getAttribute('poster');
			if (!poster) return Promise.resolve();

			return new Promise((resolve) => {
				const timer = setTimeout(resolve, this.config.timing.posterTimeout);
				const done = () => {
					clearTimeout(timer);
					resolve();
				};
				const img = new Image();
				img.onload = done;
				img.onerror = done;
				img.src = poster;
				if (img.complete) done();
			});
		};

		const loadVideo = (video) => {
			if (video.dataset.loaded === 'true' || video.dataset.loading === 'true') return;

			const sources = video.querySelectorAll(this.config.selectors.videoSource);
			const pending = [...sources].filter((source) => source.dataset.src);
			if (!pending.length) return;

			video.dataset.loading = 'true';

			whenPosterReady(video).then(() => {
				delete video.dataset.loading;

				// The video may have scrolled out (or the breakpoint changed) while we waited.
				if (!video.classList.contains(this.config.classes.videoLoaded)) return;

				pending.forEach((source) => {
					source.src = source.dataset.src;
				});
				video.preload = 'metadata';
				video.dataset.loaded = 'true';

				video.load();
				video.play().catch((err) =>
					window.log('warn', 'VideoManager', 'Video auto-play failed', { error: err.message })
				);
			});
		};

		const unloadVideo = (video) => {
			delete video.dataset.loading;
			if (video.dataset.loaded === 'true') {
				video.pause();
				video.currentTime = 0;
				// Clear sources to release memory (data-src is kept so loadVideo can restore them)
				video.querySelectorAll(this.config.selectors.videoSource).forEach((source) => {
					source.removeAttribute('src');
				});
				video.load();
				video.dataset.loaded = 'false';
			}
		};

		const handleVideoVisibility = (video) => {
			const isMobileView = window.matchMedia(`(max-width: ${window.MOBILE_BREAKPOINT}px)`).matches;
			const isDesktop = video.classList.contains('mm-desktop');
			const isMobileVideo = video.classList.contains('mm-mobile');

			const shouldPlay = (!isDesktop && !isMobileVideo) ||
								(isDesktop && !isMobileView) ||
								(isMobileVideo && isMobileView);

			if (shouldPlay) {
				loadVideo(video);
				video.classList.add(this.config.classes.videoLoaded);
			} else {
				video.classList.remove(this.config.classes.videoLoaded);
			}
		};

		// Intersection Observer
		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					handleVideoVisibility(entry.target);
				} else {
					unloadVideo(entry.target);
				}
			});
		}, {
			rootMargin: '50px 0px',
			threshold: 0.1
		});

		videos.forEach((video) => observer.observe(video));

		// Resize handling — remove previous handler before adding new one to avoid accumulation
		if (this._resizeHandler) {
			window.removeEventListener('resize', this._resizeHandler);
		}
		this._resizeHandler = window.debounce(() => {
			videos.forEach((video) => {
				if (video.classList.contains(this.config.classes.videoLoaded)) {
					handleVideoVisibility(video);
				}
			});
		}, this.config.timing.resizeDebounce);

		window.addEventListener('resize', this._resizeHandler);

		window.emit('global:video-manager-initialized');
	}
};

/*********************************************************************************************
 * 🌍 GLOBAL ACCESS
 */
window.VideoManager = VideoManager;

/*********************************************************************************************
 * 🔄 BACKWARD COMPATIBILITY
 */
window.mmInitVideoLazy = () => VideoManager.init();

/*********************************************************************************************
 * 🎯 AUTO-INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	VideoManager.init();
});
